import { useCallback, useMemo } from 'react'
import {
  ACTIONS,
  type ActionKey,
  type ModuleKey,
  type Permission,
  permission,
  wildcardOf,
} from '@/constants/permissions'
import { useAuth } from '@/providers/authContext'

/**
 * The ACL hook. Drives route access and UI visibility (nav, buttons, columns,
 * filters). Client-side checks are UX only — the backend enforces.
 *
 * Keys come from `src/constants/permissions.ts` in the backend's own
 * `<module>.<action>` format, so a granted `customer.edit` matches
 * `canModule(MODULES.customers, ACTIONS.edit)` directly.
 *
 * The session set also holds each key's normalised form (`transactions:view`)
 * alongside the raw one — see `normalizeApiPermission` there — so a check
 * succeeds whichever vocabulary the backend settles on. That alias map is
 * best-effort until the backend publishes its full permission list
 * (`src/screens/users/agent.md` §23).
 */
export interface PermissionApi {
  /** Exact key check, e.g. `can('reservations:edit-past')`. */
  readonly can: (permission: Permission) => boolean
  /** Any of the given keys. */
  readonly canAny: (permissions: readonly Permission[]) => boolean
  /** All of the given keys. */
  readonly canAll: (permissions: readonly Permission[]) => boolean
  /** Module CRUD shorthand: `canModule('customers', 'create')`. */
  readonly canModule: (module: ModuleKey, action?: ActionKey) => boolean
  /** True when the module is visible at all (has `view`). */
  readonly canViewModule: (module: ModuleKey) => boolean
  readonly isSuperadmin: boolean
}

export function usePermission(): PermissionApi {
  const { user, permissions } = useAuth()
  const isSuperadmin = user?.isSuperadmin ?? false

  // The API can grant a whole module at once (`customer.*` → `customers:*`),
  // which covers every action on it, sensitive keys included.
  const can = useCallback(
    (permission: Permission) =>
      isSuperadmin || permissions.has(permission) || permissions.has(wildcardOf(permission)),
    [isSuperadmin, permissions],
  )

  /*
   * Both delegate to `can` rather than testing the set themselves. They used to
   * call `permissions.has(p)` directly, which silently skipped the `module.*`
   * wildcard branch — so a role granted `cms.*` passed `can('cms.manage')` but
   * failed `canAny(['cms.manage'])`, and the sidebar row appeared while the
   * route it pointed at answered 403. One rule, one place.
   */
  const canAny = useCallback(
    (list: readonly Permission[]) => (isSuperadmin ? true : list.some(can)),
    [can, isSuperadmin],
  )

  const canAll = useCallback(
    (list: readonly Permission[]) => (isSuperadmin ? true : list.every(can)),
    [can, isSuperadmin],
  )

  const canModule = useCallback(
    (module: ModuleKey, action: ActionKey = ACTIONS.view) => can(permission(module, action)),
    [can],
  )

  const canViewModule = useCallback(
    (module: ModuleKey) => canModule(module, ACTIONS.view),
    [canModule],
  )

  return useMemo(
    () => ({ can, canAny, canAll, canModule, canViewModule, isSuperadmin }),
    [can, canAny, canAll, canModule, canViewModule, isSuperadmin],
  )
}
